import { memo } from 'react';
import { Button, Badge } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import IntegrationProviderLogo from './IntegrationProviderLogo.jsx';
import StatusPill from './StatusPill.jsx';
import GateBanner from './GateBanner.jsx';
import { useIntegrationsPermissions } from '../hooks';
import { useIntegrations } from '../context';
import { formatRelativeTime, catalogCategoryLabel } from '../utils';
import { Sparkles, KeyRound, Unplug, LockKeyhole, Lock, Clock, User } from 'lucide-react';

// Prototype `.btn-ai`: cyan → violet + soft cyan glow (overrides shared gradient).
const ASK_AI_CLASS = [
  '!bg-[linear-gradient(135deg,#06B6D4_0%,#A78BFA_100%)] !text-white !border-0',
  '!shadow-[0_2px_10px_rgba(6,182,212,0.25)] hover:!shadow-[0_4px_16px_rgba(6,182,212,0.40)]',
  'hover:!-translate-y-px',
].join(' ');

// Prototype `.btn-danger` (ghost-danger): tinted surface, red text/border — not solid fill.
const DISCONNECT_CLASS = [
  '!border !border-red-500/20 !bg-red-500/10 !text-red-400',
  'hover:!bg-red-500/18 hover:!text-red-300',
].join(' ');

// Prototype `.detail-cat-chip`: fixed blue chrome; category token only tints text/dot.
// Alpha is baked into the arbitrary value (`[rgba(...)]`) — the `[#hex]/opacity`
// modifier form does not generate reliably here and falls back to a default
// (light) border, which read as an unwanted white outline.
const CATEGORY_CHIP_CLASS = [
  'inline-flex items-center gap-[5px] rounded-lg border border-[rgba(43,127,255,0.28)] bg-[rgba(43,127,255,0.10)]',
  'px-[9px] py-[3px] text-[10.5px] font-bold uppercase leading-none tracking-[0.06em]',
].join(' ');

// Prototype source / OFFICIAL chip — soft blue outline (Badge informative + overrides).
const OFFICIAL_BADGE_CLASS = [
  '!h-auto !gap-0 !border-[rgba(33,115,255,0.45)] !bg-[rgba(33,115,255,0.10)] !px-2 !py-[3px]',
  '!text-[10px] !font-bold !uppercase !leading-none !tracking-[0.06em] !text-[#7dd3fc]',
].join(' ');

// Prototype `.detail-meta-pill`.
const META_PILL_CLASS = [
  'inline-flex items-center gap-[5px] rounded-lg border border-white/[0.06] bg-white/[0.03]',
  'px-[9px] py-1 text-[11.5px] font-medium text-content-secondary',
  'dark:border-white/[0.06] dark:bg-white/[0.03]',
].join(' ');

function MetaPill({ icon: Icon, children }) {
  return (
    <span className={META_PILL_CLASS}>
      <Icon size={11} aria-hidden="true" className="shrink-0 text-content-faint" />
      {children}
    </span>
  );
}

/**
 * Provider Detail sticky header (CT-4178) — faithful to prototype `.detail-head`.
 * Horizontal/vertical inset is 1.5rem (`p-6`); the page does not add a second
 * max-width gutter. Banner sits above the sticky identity row.
 */
function ProviderHeader({
  provider,
  onAskAI,
  onRotate,
  onDisconnect,
  onConnect,
  onReview,
  onOpenProvider,
}) {
  const { t } = useTranslation('admin-studio');
  const { canManageIntegrations } = useIntegrationsPermissions();
  const { openRequestIntegration } = useIntegrations();

  return (
    <div className="flex flex-col gap-3">
      {provider.isConnected ? (
        <GateBanner
          provider={provider}
          onRotate={onRotate}
          onReview={onReview}
          onOpenProvider={onOpenProvider}
          canManage={canManageIntegrations}
        />
      ) : null}

      <div>
        <div className="flex flex-wrap items-start gap-4">
          <IntegrationProviderLogo slug={provider.slug} name={provider.name} size="large" />

          <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
            <div className="mb-0.5 flex flex-wrap items-center gap-2 text-[11.5px] text-content-faint">
              <span className={[CATEGORY_CHIP_CLASS, provider.categoryColorClass].join(' ')}>
                <span aria-hidden="true" className="h-[5px] w-[5px] rounded-full bg-current" />
                {catalogCategoryLabel(provider.category, t)}
              </span>

              {provider.isConnected ? (
                <StatusPill status={provider.status} activeVariant surface="detail" />
              ) : null}

              {provider.isConnected && provider.lastRun ? (
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <span
                    aria-hidden="true"
                    className="h-[5px] w-[5px] rounded-full bg-emerald-500 shadow-[0_0_0_2px_rgba(16,185,129,0.18)]"
                  />
                  {t('integrations.provider.header.lastRun', {
                    time: formatRelativeTime(provider.lastRun, t),
                  })}
                </span>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-[9px]">
              <h1 className="text-2xl font-bold leading-[1.2] tracking-[-0.015em] text-content">
                {provider.name}
              </h1>
              {provider.official ? (
                <Badge
                  variant="informative"
                  size="small"
                  radius="default"
                  className={OFFICIAL_BADGE_CLASS}
                >
                  {t('integrations.provider.official')}
                </Badge>
              ) : null}
            </div>

            {provider.vendor ? (
              <p className="text-[13px] font-medium text-content-secondary">{provider.vendor}</p>
            ) : null}

            {provider.isConnected ? (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {provider.authMethod ? (
                  <MetaPill icon={Lock}>
                    {t(`integrations.provider.auth.${provider.authMethod}`)}
                  </MetaPill>
                ) : null}
                {provider.connectedAt ? (
                  <MetaPill icon={Clock}>
                    {t('integrations.provider.header.connected')}{' '}
                    <b className="font-semibold text-content">
                      {formatRelativeTime(provider.connectedAt, t)}
                    </b>
                  </MetaPill>
                ) : null}
                {provider.connectedBy ? (
                  <MetaPill icon={User}>
                    {t('integrations.provider.header.by')}{' '}
                    <b className="font-semibold text-content">{provider.connectedBy.name}</b>
                  </MetaPill>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
            <Button
              variant="gradient"
              size="sm"
              leftIcon={Sparkles}
              onClick={onAskAI}
              className={ASK_AI_CLASS}
            >
              {t('integrations.provider.actions.askAI')}
            </Button>

            {/* Request Access — end users only (CT-4185). Dormant in V1 (RBAC = CT-3900). */}
            {!canManageIntegrations && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={LockKeyhole}
                onClick={() => openRequestIntegration({ prefillName: provider.name })}
              >
                {t('integrations.provider.actions.requestAccess')}
              </Button>
            )}

            {provider.isConnected ? (
              canManageIntegrations && (
                <>
                  <Button variant="secondary" size="sm" leftIcon={KeyRound} onClick={onRotate}>
                    {t('integrations.provider.actions.rotate')}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    leftIcon={Unplug}
                    onClick={onDisconnect}
                    className={DISCONNECT_CLASS}
                  >
                    {t('integrations.provider.actions.disconnect')}
                  </Button>
                </>
              )
            ) : (
              <Button variant="classicPrimary" size="sm" onClick={onConnect}>
                {t('integrations.provider.connect', { name: provider.name })}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(ProviderHeader);
